import { Grid } from "@mui/material";
import {
  TextArea,
  TextInput,
  DropdownOption,
  Toggle,
  DropdownType,
  Dropdown,
  Typography,
  LoadingSpinner,
  Tooltip,
} from "cprime-foundational-components";
import { createMultiSelectRenderOption } from "../Common/multiSelectRenderOption";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import * as style from "./CreateEditRolesDetails.module.scss";
import { useParams, useLocation } from "react-router-dom";
import {
  getRoleSetupDrodownData,
  getRoleDetailsByRoleId,
  getRoleContextDetails,
} from "../../services/RequestAccess/RequestAccess";
import { useSelector, useDispatch } from "react-redux";
import {
  selectCurrentRoleId,
  setCurrentRoleId,
  updateCreateRole,
  updateRolesDropdownData,
  selectNamesData,
  CreateRoleState,
} from "../../store/slices/CreateRoleSlice";
import EditIcon from "@mui/icons-material/Edit";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RoleConditionsFilters, {
  FilterCondition,
} from "./RoleConditionsFilters";
import { debounce } from "lodash";
import { searchTokenRoleName } from "../../store/slices/userGroupSlice";

interface CreateEditRolesDetailsProps {
  isEditable?: boolean;
  error?: any[];
  protected_by_name?: string;
  roleDetailsEdit: boolean;
  setRoleDetailsEdit: React.Dispatch<React.SetStateAction<boolean>>;
  roleConditionsEdit: boolean;
  setRoleConditionsEdit: React.Dispatch<React.SetStateAction<boolean>>;
}

const CreateEditRolesDetails: React.FC<CreateEditRolesDetailsProps> = ({
  isEditable = false,
  error = [],
  protected_by_name,
  roleDetailsEdit,
  setRoleDetailsEdit,
  roleConditionsEdit,
  setRoleConditionsEdit,
}) => {
  const scssClass = style as any;
  const { id, roleId, action } = useParams();

  const location = useLocation();
  const cloneRoleId = location?.state?.cloneRoleId?.[0]?.value;
  const isCloneMode = location?.state?.action;
  const isEditMode = action === "edit-role" && !!roleId;
  const dispatch = useDispatch();
  const currentRoleId = useSelector(selectCurrentRoleId);
  const [roleName, setRoleName] = useState("");
  const [objectId, setObjectId] = useState("");
  const [sailpointRoleName, setSailpointRoleName] = useState("");
  const [description, setDescription] = useState("");
  const [toggleState, setToggleState] = useState(false);
  const [dropdownFetchStatus, setDropdownFetchStatus] = useState("idle");
  const namesData = useSelector(selectNamesData);

  const [regionOptions, setRegionOptions] = useState<DropdownOption[]>([]);
  const [fleetOptions, setFleetOptions] = useState<DropdownOption[]>([]);
  const [dealerOptions, setDealerOptions] = useState<DropdownOption[]>([]);
  const [distributionOptions, setDistributionOptions] = useState<
    DropdownOption[]
  >([]);
  const [plantOptions, setPlantOptions] = useState<DropdownOption[]>([]);
  const [lifecycleStatusOptions, setLifecycleStatusOptions] = useState<
    DropdownOption[]
  >([]);
  const [filters, setFilters] = useState<FilterCondition[]>([]);
  const [roleConditions, setRoleConditions] = useState<any[]>([]);
  const [contextOptions, setContextOptions] = useState<DropdownOption[]>([]);
  const [selectedContexts, setSelectedContexts] = useState<DropdownOption[]>(
    [],
  );
  const [roleConditionsPreview, setRoleConditionsPreview] = useState([]);
  const [isRoleDetailsLoading, setIsRoleDetailsLoading] = useState(false);
  const [isSailPointLoading, setIsSailPointLoading] = useState<boolean>(false);
  // const [formValues, setFormValues] = useState<{ sailpointRoleName: string }>({
  //   sailpointRoleName: "",
  // });
  const [sailPointOptions, setSailPointOptions] = useState<DropdownOption[]>(
    [],
  );
  const [sailPointText, setSailPointText] = useState<string>("");

  const sailPointClearedRef = useRef<boolean>(false);
  const sailPointSelectedRef = useRef<boolean>(false);
  const sailPointRequestIdRef = useRef<number>(0);
  const sailPointAbortControllerRef = useRef<AbortController | null>(null);
  const debouncedSailPointRef = useRef<any>(null);

  // const [dropdownFormData, setDropdownFormData] = useState({
  //   region: "",
  //   fleet: "",
  //   dealerName: "",
  //   distribution: "",
  //   plant: "",
  //   lifecycleStatus: "",
  // });

  const getContextDetails = async () => {
    try {
      const appId = Number(id?.split("-")[1]);
      const response = await getRoleContextDetails(appId);
      if (response?.status === "SUCCESS" && Array.isArray(response.data)) {
        const mappedContexts = response.data.map((ctx: any) => ({
          label: ctx.contextName,
          value: ctx.contextId.toString(),
          id: ctx.contextId.toString(),
        }));
        setContextOptions(mappedContexts);
        return mappedContexts;
      }
    } catch (error) {
      // console.error("Error fetching context details", error);
    }
    return [];
  };

  const fetchSailPointOptions = useCallback(async (query: string) => {
    try {
      if (!id?.split("-")?.[1]) {
        return;
      }
      // Increment request ID to invalidate previous requests
      const currentRequestId = ++sailPointRequestIdRef.current;

      // Abort previous request if still pending
      if (sailPointAbortControllerRef.current) {
        sailPointAbortControllerRef.current.abort();
      }

      // Create new abort controller for this request
      const abortController = new AbortController();
      sailPointAbortControllerRef.current = abortController;

      setIsSailPointLoading(true);
      const response = await searchTokenRoleName(
        `rolesetup/search-app-roles/${id?.split("-")?.[1]}?searchTerm=${query}`,
      );

      // Ignore response if a newer request has been made
      if (currentRequestId !== sailPointRequestIdRef.current) {
        return;
      }

      const mapped: any[] = (response?.data || []).map(
        (idRole: { groupId: string; groupName: string; idpRole: string }) => ({
          id: idRole?.idpRole,
          label: idRole?.idpRole,
          displayName: idRole?.idpRole,
          groupId: idRole?.groupId,
        }),
      );

      setSailPointOptions(mapped);
    } catch (error) {
      // Ignore abort errors (user initiated cancellation)
      if (error instanceof Error && error.name === "AbortError") {
        return;
      }
      setSailPointOptions([]);
    } finally {
      setIsSailPointLoading(false);
    }
  }, []);

  // Create debounced function with proper cleanup
  const debouncedFetchSailPointOptions = useMemo(() => {
    const fn = debounce((val: string) => {
      fetchSailPointOptions(val);
    }, 300);
    debouncedSailPointRef.current = fn;
    return fn;
  }, [fetchSailPointOptions]);

  // Cleanup debounced functions and abort pending requests on unmount
  useEffect(() => {
    return () => {
      debouncedSailPointRef.current?.cancel?.();
    };
  }, []);

  // Inject a "new option" entry when the typed text is not in the fetched list
  const sailPointOptionsWithNew = useMemo(() => {
    const trimmed = sailPointText?.trim();
    if (
      trimmed &&
      !sailPointOptions.some(
        (opt) => opt.label?.toLowerCase() === trimmed.toLowerCase(),
      )
    ) {
      return [
        ...sailPointOptions,
        { id: trimmed, label: trimmed, isNew: true } as any,
      ];
    }
    return sailPointOptions;
  }, [sailPointText, sailPointOptions]);

  useEffect(() => {
    const applicationId = id?.split("-")?.[1];
    if (applicationId) {
      getDropdownDataById(applicationId);
    }
  }, [id]);
  const getErrorMessage = (fieldName: any) => {
    const fieldError = error?.find((e) => e.field === fieldName);
    return fieldError ? fieldError?.message : "";
  };

  useEffect(() => {
    const fetchData = async () => {
      // Fetch all available contexts first
      setIsRoleDetailsLoading(true);
      const allContexts = await getContextDetails();
      const role_id =
        isEditMode && roleId ? roleId.split("-")[1] : currentRoleId;

      if (cloneRoleId) {
        const roleResponse = await getRoleDetailsByRoleId(cloneRoleId);
        const roleData = roleResponse?.data;
        if (roleData) {
          setRoleName(namesData?.roleName || "");
          setObjectId(namesData?.objectId || "");
          setSailpointRoleName(namesData?.sailpointRoleName || "");
          setSailPointText(namesData?.sailpointRoleName || "");
          debouncedFetchSailPointOptions(namesData?.sailpointRoleName?.trim());

          // setFormValues((prevValues) => ({
          //   ...prevValues,
          //   sailpointRoleName: namesData?.sailpointRoleName || "",
          // }));
          setDescription(namesData?.description ?? roleData?.description ?? "");
          setToggleState(
            (
              namesData?.isAutoApprove ?? roleData?.isAutoApprove
            )?.toLowerCase() === "true",
          );

          // If role has saved contexts, mark them as selected
          const contextSource = namesData?.context ?? roleData?.context ?? [];
          if (Array.isArray(contextSource) && contextSource.length > 0) {
            const selected = allContexts.filter((ctx: { value: any }) =>
              contextSource.includes(Number(ctx.value)),
            );
            setSelectedContexts(selected);
          }

          const roleConditionsSource =
            namesData?.roleConditions ?? roleData?.roleConditions ?? [];
          const mappedFilters: FilterCondition[] =
            roleConditionsSource.map((rc: any) => {
              const dropdownOptions = getDropdownOptionsForField(rc.fieldName);
              const selectedOption = dropdownOptions.find((opt) =>
                rc.values.includes(opt.value),
              );

              return {
                field: rc.fieldName,
                operator: rc.operator,
                value: selectedOption
                  ? {
                      label: selectedOption.label,
                      value: selectedOption.value,
                      id: selectedOption.id,
                    }
                  : { label: "", value: "", id: "" },
                andOrOperator: rc.andOrOperator,
              };
            }) || [];

          setFilters(mappedFilters);
          setRoleConditions(roleConditionsSource);
          setFilters(mappedFilters);
          setRoleConditions(roleData.roleConditions || []);
        }
      } else if (role_id) {
        const roleResponse = await getRoleDetailsByRoleId(role_id);
        const roleData = roleResponse?.data;

        if (roleData) {
          setRoleName(roleData.roleName || "");
          setObjectId(roleData.objectId || "");
          setSailpointRoleName(roleData.sailpointRoleName || "");
          setSailPointText(roleData.sailpointRoleName || "");
          debouncedFetchSailPointOptions(roleData?.sailpointRoleName?.trim());
          setDescription(roleData.description || "");
          setToggleState(roleData.isAutoApprove?.toLowerCase() === "true");

          // If role has saved contexts, mark them as selected
          if (
            Array.isArray(roleData.context) &&
            roleData?.context?.length > 0
          ) {
            const selected = allContexts.filter((ctx: { value: any }) =>
              roleData.context.includes(Number(ctx.value)),
            );
            setSelectedContexts(selected);
          }

          // Existing role conditions mapping logic
          const mappedFilters: FilterCondition[] =
            roleData.roleConditions?.map((rc: any) => {
              const dropdownOptions = getDropdownOptionsForField(rc.fieldName);
              const selectedOption = dropdownOptions.find((opt) =>
                rc.values.includes(opt.value),
              );
              return {
                //id: Date.now().toString() + rc.fieldName,
                field: rc.fieldName,
                operator: rc.operator,
                value: selectedOption
                  ? {
                      label: selectedOption.label,
                      value: selectedOption.value,
                      id: selectedOption.id,
                    }
                  : { label: "", value: "", id: "" },
                andOrOperator: rc.andOrOperator,
              };
            }) || [];

          setFilters(mappedFilters);
          setRoleConditions(roleData.roleConditions || []);
        }
      }
      setIsRoleDetailsLoading(false);
    };
    if (dropdownFetchStatus === "succeeded") {
      fetchData();
    }

    roleId?.split("-")[1] &&
      dispatch(setCurrentRoleId(Number(roleId.split("-")[1])));
  }, [id, roleId, isEditMode, currentRoleId, dropdownFetchStatus]);

  const getDropdownOptionsForField = (fieldName: string): DropdownOption[] => {
    switch (fieldName) {
      case "Region":
        return regionOptions;
      case "Fleet":
        return fleetOptions;
      case "Dealer Name":
        return dealerOptions;
      case "Distribution":
        return distributionOptions;
      case "Plant":
        return plantOptions;
      case "Lifecycle Status":
        return lifecycleStatusOptions;
      default:
        return [];
    }
  };

  const getDropdownDataById = async (app_id: any) => {
    try {
      setDropdownFetchStatus("loading");
      const response = await getRoleSetupDrodownData(app_id);
      const data = response?.data;

      if (!data || typeof data !== "object") {
        return;
      }

      const cleanOptions = (
        array: any[],
        labelField: string,
      ): DropdownOption[] => {
        return (array || [])
          ?.filter((item) => item?.[labelField]?.trim() !== "")
          ?.map((item) => ({
            value: item?.id?.toString(),
            label: item?.[labelField],
            id: item?.id?.toString(),
          }));
      };

      const cleanedData = {
        regions: cleanOptions(data?.regions, "region_name"),
        fleets: cleanOptions(data?.fleets, "fleet_name"),
        dealers: cleanOptions(data?.dealers, "dealer_name"),
        distributions: cleanOptions(data?.distributions, "distribution_name"),
        plants: cleanOptions(data?.plants, "plant_name"),
        lifecycleStatuses: cleanOptions(
          data?.lifecycleStatuses,
          "lifecycle_status_name",
        ),
      };

      dispatch(updateRolesDropdownData(cleanedData));

      setRegionOptions(cleanedData.regions);
      setFleetOptions(cleanedData.fleets);
      setDealerOptions(cleanedData.dealers);
      setDistributionOptions(cleanedData.distributions);
      setPlantOptions(cleanedData.plants);
      setLifecycleStatusOptions(cleanedData.lifecycleStatuses);
      setDropdownFetchStatus("succeeded");
    } catch (error) {
      setDropdownFetchStatus("failed");
    }
  };

  useEffect(() => {
    const payload = {
      appId: Number(id?.split("-")[1]),
      roleName,
      sailpointRoleName,
      description,
      status: "draft",
      isAutoApprove: toggleState?.toString(),
      roleConditions,
      context: selectedContexts.map((ctx) => Number(ctx.value)),
      // Only include objectId when it has a real value
      ...(objectId !== undefined &&
      objectId !== null &&
      objectId !== "" &&
      objectId !== "undefined"
        ? { objectId }
        : {}),
    } as CreateRoleState;
    dispatch(updateCreateRole(payload));
  }, [
    roleName,
    filters,
    objectId,
    toggleState,
    sailpointRoleName,
    description,
    // dropdownFormData,
    dispatch,
    id,
    roleConditions,
    selectedContexts,
  ]);

  const RoleConditions = ({ conditions }: any) => {
    const renderConditions = (conditions: any) => {
      return conditions.map(
        (
          condition: {
            operator: string;
            value: any[];
            field: any;
            logicalOperator: any;
            regionMode: any;
          },
          index: React.Key | null | undefined,
        ) => {
          // Determine the operator symbol based on the 'operator' field
          const operatorSymbol = condition.operator === "notEqual" ? "!=" : "=";
          // Get the labels from the 'value' array and join them with commas
          let values = "";
          if (!condition?.value || condition?.value?.length === 0) {
            values = condition?.regionMode;
          } else {
            const labels = condition?.value
              ?.map((v) => v?.label)
              .filter(Boolean);
            values =
              labels?.length > 0 ? labels.join(", ") : condition?.regionMode;
          }

          // Construct the condition text
          let conditionText = `${condition.field} ${operatorSymbol} ${values}`;

          // If there's a logical operator (AND/OR), prepend it
          // if (condition.logicalOperator) {
          //   conditionText = `${condition.logicalOperator} ${conditionText}`;
          // }

          return (
            <div key={index} className={scssClass.roleConditionPreviewRow}>
              <div
                className="condition-text"
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    color: "#58595B",
                    fontSize: "14px",
                    //background: "#F1F2F4",
                    display: "flex",
                    width: "16px",
                  }}
                >
                  {Number(index) === 0 ? (
                    <FilterAltIcon
                      style={{ fontSize: "16px", color: "#58595B" }}
                    />
                  ) : (
                    <span style={{ padding: "4px" }}>{Number(index) + 1}</span>
                  )}
                </span>{" "}
                <span
                  style={{
                    fontSize: "14px",
                    padding: "4px 10px",
                    display: "flex",
                    gap: "8px",
                  }}
                >
                  {condition?.logicalOperator && (
                    <span style={{ color: "#58595B" }}>
                      {condition?.logicalOperator}
                    </span>
                  )}{" "}
                  {conditionText}
                </span>
              </div>
            </div>
          );
        },
      );
    };

    return <div>{renderConditions(conditions)}</div>;
  };

  return (
    <Grid sx={{ width: "100%" }}>
      {isRoleDetailsLoading ? (
        <LoadingSpinner fullScreen={true} />
      ) : (
        <>
          <div
            style={{
              padding: "0.5rem 1rem",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <p
              style={{
                color: "#000",
                fontSize: "0.87rem",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "1rem",
                margin: 0,
              }}
            >
              Role Details
            </p>
            {isEditable && (
              <span
                onClick={() => setRoleDetailsEdit(true)}
                style={{ cursor: "pointer" }}
                role="button"
                data-testid="role-details-edit"
                tabIndex={0}
              >
                {isEditMode && <EditIcon style={{ fontSize: "1rem" }} />}
              </span>
            )}
          </div>
          <Grid container spacing={2} className={scssClass.createContainer}>
            <Grid item xs={4} className={scssClass.gridInputField}>
              <Typography className={scssClass.label}>Role Name</Typography>
              {(!isEditMode || (isEditMode && roleDetailsEdit)) && (
                <TextInput
                  label={""}
                  style={{ width: "98%" }}
                  placeholder="Role Name"
                  value={roleName}
                  onChange={(e) => setRoleName(e?.target?.value)}
                  error={cloneRoleId ? !!getErrorMessage("roleName") : false}
                  errorMessage={cloneRoleId ? getErrorMessage("roleName") : ""}
                />
              )}
              {isEditMode && !roleDetailsEdit && (
                <Typography className={scssClass.value}>{roleName}</Typography>
              )}
            </Grid>
            {protected_by_name === "AzureAD" ? (
              <Grid item xs={4} className={scssClass.gridInputField}>
                <Typography className={scssClass.label}>
                  ID Token Role Name
                </Typography>
                {(!isEditMode || (isEditMode && roleDetailsEdit)) && (
                  <Dropdown
                    style={{ width: "100%" }}
                    error={
                      cloneRoleId
                        ? !!getErrorMessage("sailpointRoleName")
                        : false
                    }
                    placeholder="Select"
                    optionsTextLimit={200}
                    aria-label="sailpointRoleNameselect"
                    componentsProps={{
                      paper: {
                        sx: {
                          backgroundColor: "#ffffff",
                          "& .MuiAutocomplete-option": {
                            color: "#000000",
                            backgroundColor: "#ffffff !important",
                          },
                        },
                      },
                    }}
                    options={sailPointOptionsWithNew}
                    renderOption={(props: any, option: any) => {
                      const isNewOption =
                        sailPointText?.trim().length > 0 &&
                        !sailPointOptions.some(
                          (o) =>
                            o.label?.toLowerCase() ===
                            option.label?.toLowerCase(),
                        );
                      return (
                        <li {...props}>
                          {isNewOption ? (
                            <Tooltip
                              title="User-provided value"
                              placement="right"
                              arrow
                            >
                              <span
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "100%",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span>{option.label}</span>
                                <AddCircleOutlineIcon
                                  style={{
                                    fontSize: "16px",
                                    color: "#000000",
                                    marginLeft: "8px",
                                    flexShrink: 0,
                                  }}
                                />
                              </span>
                            </Tooltip>
                          ) : (
                            <span>{option.label}</span>
                          )}
                        </li>
                      );
                    }}
                    inputValue={sailPointText}
                    onInputChange={(_, value) => {
                      if (sailPointClearedRef.current) {
                        sailPointClearedRef.current = false;
                        setSailPointText("");
                        setSailPointOptions([]);
                        return;
                      }
                      if (sailPointSelectedRef.current) {
                        sailPointSelectedRef.current = false;
                        setSailPointText(value);
                        return;
                      }
                      setSailPointText(value);
                      if (value?.trim()) {
                        debouncedFetchSailPointOptions(value?.trim());
                      } else {
                        setIsSailPointLoading(false);
                        setSailPointOptions([]);
                      }
                    }}
                    onClose={() => {
                      // If selection just happened, skip free-text commit
                      if (sailPointSelectedRef.current) {
                        sailPointSelectedRef.current = false;
                        return;
                      }
                      if (sailPointClearedRef.current) {
                        return;
                      }
                      const trimmed = sailPointText?.trim();
                      if (
                        trimmed &&
                        !sailPointOptions.some(
                          (opt) =>
                            opt.label?.toLowerCase() === trimmed.toLowerCase(),
                        )
                      ) {
                        // Free-typed value — treat as valid selection
                        setSailpointRoleName(trimmed);
                        setObjectId(""); // No groupId for free-typed value
                      }
                    }}
                    onChange={(selected: any[]) => {
                      if (selected?.length > 0 && selected[0]?.label) {
                        const selectedApprover = String(selected[0].label);
                        const selectedId = selected[0]?.id;
                        sailPointSelectedRef.current = true;
                        setSailPointText(selectedApprover);
                        setSailpointRoleName(String(selectedId));
                        setObjectId(String(selected[0]?.groupId));
                      } else {
                        sailPointClearedRef.current = true;
                        setSailPointText("");
                        setSailpointRoleName("");
                        setObjectId("");
                      }
                    }}
                    selectedOptions={
                      sailPointText
                        ? [{ id: sailPointText, label: sailPointText }]
                        : []
                    }
                    multiple={false}
                    type={DropdownType.FilterSingleSelect}
                  />
                )}
                {isEditMode && !roleDetailsEdit && (
                  <Typography className={scssClass.value}>
                    {sailpointRoleName}
                  </Typography>
                )}
              </Grid>
            ) : (
              <Grid item xs={4} className={scssClass.gridInputField}>
                <Typography className={scssClass.label}>
                  ID Token Role Name
                </Typography>
                {(!isEditMode || (isEditMode && roleDetailsEdit)) && (
                  <TextInput
                    label={""}
                    style={{ width: "98%" }}
                    placeholder="ID Token Role Name"
                    value={sailpointRoleName}
                    onChange={(e) => setSailpointRoleName(e?.target?.value)}
                    error={
                      cloneRoleId
                        ? !!getErrorMessage("sailpointRoleName")
                        : false
                    }
                    errorMessage={
                      cloneRoleId ? getErrorMessage("sailpointRoleName") : ""
                    }
                  />
                )}
                {isEditMode && !roleDetailsEdit && (
                  <Typography className={scssClass.value}>
                    {sailpointRoleName}
                  </Typography>
                )}
              </Grid>
            )}
            {protected_by_name !== "AzureAD" && (
              <Grid item xs={4} className={scssClass.gridInputField}>
                <Typography className={scssClass.label}>
                  Role Object ID
                </Typography>
                {(!isEditMode || (isEditMode && roleDetailsEdit)) && (
                  <TextInput
                    label={""}
                    style={{ width: "98%" }}
                    placeholder="Role Object ID"
                    value={objectId}
                    onChange={(e) => setObjectId(e?.target?.value)}
                    error={cloneRoleId ? !!getErrorMessage("objectId") : false}
                    errorMessage={
                      cloneRoleId ? getErrorMessage("objectId") : ""
                    }
                  />
                )}
                {isEditMode && !roleDetailsEdit && (
                  <Typography className={scssClass.value}>
                    {objectId}
                  </Typography>
                )}
              </Grid>
            )}
            <Grid item xs={4} className={scssClass.gridInputField}>
              <Typography className={scssClass.label}>
                Context (Optional)
              </Typography>
              {(!isEditMode || (isEditMode && roleDetailsEdit)) && (
                <Dropdown
                  label=""
                  options={contextOptions}
                  selectedOptions={selectedContexts}
                  onChange={(selected) => setSelectedContexts(selected)}
                  type={DropdownType.MultiSelect}
                  disableCloseOnSelect
                  renderOption={createMultiSelectRenderOption(
                    selectedContexts,
                    setSelectedContexts,
                  )}
                  style={{ width: "98%" }}
                />
              )}
              {isEditMode && !roleDetailsEdit && (
                <Typography className={scssClass.value}>
                  {selectedContexts?.map((i) => i.label)?.join(",")}
                </Typography>
              )}
            </Grid>
            {/* description */}
            <Grid
              item
              xs={12}
              className={scssClass.gridInputField}
              style={{ paddingBottom: "1.5rem" }}
            >
              <Typography className={scssClass.label}>Description</Typography>
              {(!isEditMode || (isEditMode && roleDetailsEdit)) && (
                <TextArea
                  label={""}
                  aria-label="minimum height"
                  minRows={3}
                  placeholder="Enter Description"
                  style={{ width: "99.5%" }}
                  value={description}
                  onChange={(e) => setDescription(e?.target?.value)}
                />
              )}
              {isEditMode && !roleDetailsEdit && (
                <Typography className={scssClass.value}>
                  {description}
                </Typography>
              )}
            </Grid>
          </Grid>
          {/* additional filters */}
          <div
            style={{
              padding: "0.5rem 1rem",
              display: "flex",
              justifyContent: "space-between",
            }}
          >
            <p
              style={{
                color: "#000",
                fontSize: "0.87rem",
                fontStyle: "normal",
                fontWeight: 600,
                lineHeight: "1rem",
                margin: 0,
              }}
            >
              Role Conditions (Optional)
            </p>
            {isEditable && (
              <span
                onClick={() => setRoleConditionsEdit(true)}
                style={{ cursor: "pointer" }}
                role="button"
                data-testid="role-condition-edit"
                tabIndex={0}
              >
                {isEditMode && <EditIcon style={{ fontSize: "1rem" }} />}
              </span>
            )}
          </div>
          <Grid className={scssClass.autoApproveContainer}>
            {isEditMode && !roleConditionsEdit && (
              <div style={{ width: "100%" }}>
                <RoleConditions conditions={roleConditionsPreview} />
              </div>
            )}
            <>
              <p
                style={{
                  color: "#58595B",
                  fontSize: "12px",
                  fontWeight: 400,
                  margin: 0,
                  display:
                    !isEditMode || (isEditMode && roleConditionsEdit)
                      ? "block"
                      : "none",
                }}
              >
                Optionally add further conditions to this Role
              </p>
              <div
                style={{
                  display:
                    !isEditMode || (isEditMode && roleConditionsEdit)
                      ? "block"
                      : "none",
                  width: "100%",
                }}
              >
                <RoleConditionsFilters
                  isRoleCloning={isCloneMode === "clone_role"}
                  filters={filters}
                  onFiltersChange={setFilters}
                  onRoleConditionsChange={setRoleConditions}
                  setRoleConditionsPreview={setRoleConditionsPreview}
                />
              </div>
            </>
          </Grid>

          {/* auto approval */}
          <p
            style={{
              color: "#000",
              fontSize: "0.87rem",
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "1rem",
              padding: "0 1rem",
            }}
          >
            Auto Approval (Optional)
          </p>
          <Grid
            container
            spacing={2}
            className={scssClass.autoApproveContainer}
          >
            <p className={scssClass.autoApproveText}>
              If Auto Approval is turned on and this Role is the default
              eligible Role for an access request, then that request will be
              automatically approved
            </p>
            <Toggle
              onChange={() => setToggleState(!toggleState)}
              checked={toggleState}
              disabled={!isEditable}
              sx={{
                overflow: "visible",
                marginBottom: "4px",
                "& .MuiSwitch-thumb": {
                  marginTop: "4px",
                },
              }}
              toggledText={toggleState ? "On" : "Off"}
              size="large"
            />
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default CreateEditRolesDetails;
